import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Empty, Table } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import http from "@/utils/http";
import api from "@/utils/api";
import NoticeTenantSummary from "./NoticeTenantSummary";

const money = (value) => value == null ? "—" : `$${Number(value) < 0 ? "-" : ""}${Math.abs(Number(value)).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const transactionMoney = (value) => value == null || Number(value) === 0 ? "" : money(value);
const balance = (value) => <strong className={Number(value) > 0 ? "notice-ledger-debit" : "notice-ledger-credit"}>{money(value)}</strong>;
const descriptions = { IN: "Invoice", P: "Payment", S: "Sys", D: "D" };

export default function LedgerDetails({ tenant }) {
  const [ledger, setLedger] = useState(null);
  const [error, setError] = useState("");
  const [attempt, setAttempt] = useState(0);
  const [tableHeight, setTableHeight] = useState(500);
  const tableHost = useRef(null);
  // Preserve each transaction's original running balance; only reverse its display position.
  const newestFirst = useMemo(() => [...(ledger?.items || [])].reverse(), [ledger]);
  const tenantId = tenant.tenantId;
  useEffect(() => {
    let active = true;
    setLedger(null);
    setError("");
    http.get(api.threeDayNoticeLedger, { params: { tenantId } }).then((response) => {
      if (!active) return;
      if (response?.code !== "1" || !response.data) setError(response?.msg || "Unable to load ledger details.");
      else setLedger(response.data);
    }).catch(() => active && setError("Unable to load ledger details. Please try again."));
    return () => { active = false; };
  }, [tenantId, attempt]);

  useEffect(() => {
    const host = tableHost.current;
    if (!host) return undefined;
    const header = host.querySelector(".ant-table-header");
    const resize = () => {
      if (host.clientHeight) setTableHeight(Math.max(1, host.clientHeight - (header?.offsetHeight || 80) - 4));
    };
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(resize);
    observer?.observe(host);
    if (header) observer?.observe(header);
    window.addEventListener("resize", resize);
    resize();
    return () => { observer?.disconnect(); window.removeEventListener("resize", resize); };
  }, [ledger, error]);

  return <div className="notice-ledger-content">
    <NoticeTenantSummary tenant={tenant} label="End Balance" amount={ledger ? money(ledger.endBalance) : "—"}
      caption={ledger ? "Last 12 months" : error ? "Details unavailable" : "Loading ledger…"} />
    {error ? <Alert showIcon type="error" message={error}
      action={<Button size="small" icon={<ReloadOutlined />} onClick={() => setAttempt((value) => value + 1)}>Retry</Button>} /> : <div className="notice-ledger-table-host" ref={tableHost}>
      <Table className="notice-detail-table notice-ledger-table" size="small" pagination={false}
        loading={!ledger} dataSource={newestFirst} rowKey="cntlNo"
        scroll={{ x: 1250, y: tableHeight }}
        locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No transactions in the last 12 months" /> }}
        columns={[
          { title: "Date", dataIndex: "date1", width: 100 },
          { title: "Desc", dataIndex: "txCode", width: 90, render: (code) => descriptions[code] || code },
          { title: "Batch", dataIndex: "batch", width: 85 },
          { title: "Check No", dataIndex: "checkNo", width: 85 },
          { title: "Invoice", dataIndex: "appyto", width: 85 },
          { title: "GL", dataIndex: "glAcct", width: 65 },
          { title: "Service", dataIndex: "serviceCode", width: 75 },
          { title: "Remark", dataIndex: "remark", width: 235 },
          { title: "Charge", dataIndex: "charge", align: "right", width: 105, render: transactionMoney },
          { title: "Payment", dataIndex: "payment", align: "right", width: 105, render: transactionMoney },
          { title: "Balance", dataIndex: "balance", align: "right", width: 120, fixed: "right", render: balance },
        ]}
        summary={() => ledger && <Table.Summary fixed="top"><Table.Summary.Row>
          <Table.Summary.Cell index={0} colSpan={8}><strong>Ending balance · {ledger.endDate}</strong></Table.Summary.Cell>
          <Table.Summary.Cell index={8} align="right">{money(ledger.totalCharge)}</Table.Summary.Cell>
          <Table.Summary.Cell index={9} align="right">{money(ledger.totalPayment)}</Table.Summary.Cell>
          <Table.Summary.Cell index={10} align="right">{balance(ledger.endBalance)}</Table.Summary.Cell>
        </Table.Summary.Row></Table.Summary>} />
    </div>}
  </div>;
}
